import { useQuery, useQueryClient } from '@tanstack/react-query';
import { update } from 'ramda';
import { useCallback, useMemo } from 'react';

import { localize } from 'common/utils';

import { getSharedMenus } from '../api';
import { DraftStatus, SharedMenu, OfferingPlatformScope } from '../types';

type Props = {
  scope: OfferingPlatformScope;
};

export const useSharedMenuData = ({ scope }: Props) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => {
    return ['shared-menu', scope.id];
  }, [scope.id]);

  const {
    data: sharedMenus,
    isLoading: loadingSharedMenus,
    error: sharedMenusError,
    refetch: refetchSharedMenus,
  } = useQuery(queryKey, () => getSharedMenus(scope), {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const updateSharedMenu = useCallback(
    (updatedSharedMenu: SharedMenu) => {
      queryClient.setQueryData(queryKey, (oldData: SharedMenu[] | undefined) => {
        if (!oldData) {
          throw new Error('No shared menus data found, this should not happen!');
        }
        const sharedMenuIndex = oldData.findIndex(sharedMenu => sharedMenu.id === updatedSharedMenu.id);
        const updatedSharedMenus = update(sharedMenuIndex, updatedSharedMenu, oldData);
        return updatedSharedMenus;
      });
    },
    [queryClient, queryKey],
  );

  const updateSharedMenuStatus = useCallback(
    (sharedMenuId: string, updatedDraftStatus: DraftStatus) => {
      queryClient.setQueryData(queryKey, (oldData: SharedMenu[] | undefined) => {
        if (!oldData) {
          throw new Error('No shared menus data found, this should not happen!');
        }

        const sharedMenuIndex = oldData.findIndex(sharedMenu => sharedMenu.id === sharedMenuId);
        const updatedSharedMenu: SharedMenu = {
          ...oldData[sharedMenuIndex],
          draft: updatedDraftStatus,
        };
        const updatedSharedMenus = update(sharedMenuIndex, updatedSharedMenu, oldData);
        return updatedSharedMenus;
      });
    },
    [queryClient, queryKey],
  );

  const alphabeticallySortedSharedMenus = useMemo(() => {
    if (!sharedMenus) {
      return [];
    }

    const localized = (menu: SharedMenu) => localize(menu.name, menu.primary_language).toLocaleLowerCase();

    return sharedMenus.sort((a, b) => {
      if (localized(a) < localized(b)) {
        return -1;
      }

      if (localized(a) > localized(b)) {
        return 1;
      }

      return 0;
    });
  }, [sharedMenus]);

  return {
    sharedMenus: alphabeticallySortedSharedMenus,
    loadingSharedMenus,
    sharedMenusError,
    refetchSharedMenus,
    updateSharedMenu,
    updateSharedMenuStatus,
  };
};
